import { Button, Card, DatePicker, Select } from "@rumbledirect/ui";
import { Form, Switch } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type {
  BrandSupportLevel,
  ServicePartner,
} from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import type { UseMutationResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  fetchDwProAccounts,
  fetchVehicleBrands,
} from "@/lib/api/resources/servicePartners";
import type { Brand } from "@rumble-it/common-lib/types/common/Brand.interface";
import { ServicePartnerTag } from "@rumble-it/common-lib/types/models/ServicePartnerTag.interface";
import { ImageUpload } from "../../../../components/form/ImageUpload";
import dayjs from "dayjs";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser.ts";
import { useMemo } from "react";
import { fetchServicePartnerTags } from "@/lib/api/resources/servicePartnerTags.ts";
import { useHasRole } from "@/lib/helpers/roles";

export type ServicepartnerFormValues = Omit<
  ServicePartner,
  | "brandSupports"
  | "tags"
  | "dwProAccount"
  | "openingHoursExceptionDates"
  | "preferredServicePartner"
> & {
  brandSupports: { brand: string; support: BrandSupportLevel }[];
  tags: string[];
  dwProAccount?: string | null;
  openingHoursExceptionDates: dayjs.Dayjs[];
  preferredServicePartner?: boolean;
};

export type ServicepartnerUpdateDto = Omit<
  ServicepartnerFormValues,
  "headerImage" | "logoImage" | "openingHoursExceptionDates"
> & {
  headerImage: string | null;
  logoImage: string | null;
  openingHoursExceptionDates: string[];
  dwProAccount?: string | null;
};

interface ServicepartnerFormProps {
  initialValues?: ServicepartnerFormValues;
  loading?: boolean;
  title?: string;
  buttonText?: string;
  mutation: UseMutationResult<null, Error, ServicepartnerUpdateDto, unknown>;
}

export const ServicepartnerForm = ({
  initialValues,
  loading,
  title = "Servicepartner",
  buttonText = "Opslaan",
  mutation,
}: ServicepartnerFormProps) => {
  const [form] = Form.useForm();
  const hasAdminRole = useHasRole("ROLE_OIDC_ADMIN");

  const currentUser = useCurrentUser();

  const hasUpdatePreferredPartnerPermission = useMemo<boolean>(
    () =>
      currentUser?.permissions?.includes(
        "admin_service_partner_preferred_partner_setting_preferences_update",
      ) ?? false,
    [currentUser],
  );

  const brandsQuery = useQuery({
    queryKey: ["vehicle-brands"],
    queryFn: () => fetchVehicleBrands(),
    select: (data) =>
      data?.member?.map((brand: Brand) => ({
        label: brand.brand,
        value: brand?.id,
      })) || [],
  });

  const tagsQuery = useQuery({
    queryKey: ["service-partner-tags"],
    queryFn: () => fetchServicePartnerTags({ pagination: false }),
    select: (data) =>
      data?.member?.map((tag: ServicePartnerTag) => ({
        label: tag.name,
        value: tag.id,
      })) || [],
  });

  const dwProAccountsQuery = useQuery({
    queryKey: ["dw-pro-accounts"],
    queryFn: fetchDwProAccounts,
    enabled: hasAdminRole,
    select: (data) => {
      const accounts = Array.isArray(data) ? data : data?.member || [];
      return accounts.map((account) => ({
        label: account.title,
        value: account.id,
      }));
    },
  });

  const handleFinish = (values: ServicepartnerFormValues) => {
    const payload: ServicepartnerUpdateDto = {
      ...values,
      headerImage: values.headerImage?.id || null,
      logoImage: values.logoImage?.id || null,
      openingHoursExceptionDates:
        values.openingHoursExceptionDates?.map((date) =>
          dayjs(date).format("YYYY-MM-DD"),
        ) || [],
    };

    if (!hasAdminRole) {
      delete payload.dwProAccount;
    }

    mutation.mutate({ ...payload });
  };

  return (
    <Card loading={loading} title={title}>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleFinish}
      >
        <Form.Item label="Headerafbeelding" name="headerImage">
          <ImageUpload />
        </Form.Item>
        <Form.Item label="Logo" name="logoImage">
          <ImageUpload />
        </Form.Item>
        <Form.Item
          label="Gesloten dagen"
          help="Op deze dagen is het niet mogelijk een afspraak in te plannen"
          name="openingHoursExceptionDates"
        >
          <DatePicker
            format="DD-MM-YYYY"
            multiple
            placeholder="Selecteer data"
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item
          label="Merk-ondersteuning"
          help="Stel per merk het ondersteuningsniveau in"
        >
          <Form.List name="brandSupports">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "brand"]}
                      rules={[{ required: true, message: "Kies een merk" }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Kies merk"
                        loading={brandsQuery.isFetching}
                        options={brandsQuery.data}
                        showSearch={{
                          optionFilterProp: "label",
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "support"]}
                      rules={[{ required: true, message: "Kies een niveau" }]}
                      style={{ width: 200, marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Kies niveau"
                        options={[
                          { value: "specialist", label: "Specialist" },
                          { value: "certified", label: "Erkend" },
                          { value: "supported", label: "Ondersteund" },
                          { value: "excluded", label: "Uitgesloten" },
                        ]}
                      />
                    </Form.Item>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                      danger
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Merk toevoegen
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.Item
          label="Gepubliceerd"
          name="published"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {hasUpdatePreferredPartnerPermission ? (
          <Form.Item
            label="Preferred service partner"
            name="preferredServicePartner"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        ) : null}
        <Form.Item label="Tags" name="tags">
          <Select
            mode="multiple"
            loading={tagsQuery.isFetching}
            options={tagsQuery.data}
            showSearch={{
              optionFilterProp: "label",
            }}
          />
        </Form.Item>
        <Form.Item
          label="DwPro account"
          name="dwProAccount"
          help={
            !hasAdminRole
              ? "Alleen gebruikers met ROLE_OIDC_ADMIN kunnen dit veld wijzigen"
              : undefined
          }
        >
          <Select
            loading={dwProAccountsQuery.isFetching}
            options={dwProAccountsQuery.data}
            disabled={!hasAdminRole}
            allowClear
            showSearch={{
              optionFilterProp: "label",
            }}
          />
        </Form.Item>
        <Form.Item label={null}>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            {buttonText}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
